import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, Send } from "lucide-react";
import { storyblokEditable } from "@storyblok/react";
import ReCAPTCHA from "react-google-recaptcha";
import { Container } from "../layout/container";
import { RenderRichtext } from "../RenderRichtext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ContactFormStoryblok } from "@/types/sb-component-types";
import { useScopedI18n } from "@/locales/client";
export function ContactForm({
  blok
}: ContactFormStoryblok) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const t = useScopedI18n("contactForm");
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  useEffect(() => {
    const cookieConsent = localStorage.getItem("cookieConsent");
    setCookiesAccepted(cookieConsent === "true");
  }, []);
  const formSchema = z.object({
    name: z.string().min(2, t("nameValidation")),
    email: z.string().email(t("emailValidation")),
    message: z.string().min(10, t("messageMinValidation")).max(500, t("messageMaxValidation"))
  });
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: ""
    }
  });
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!recaptchaValue) {
      setSubmitStatus("error");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus("idle");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...data,
          recaptchaToken: recaptchaValue
        })
      });
      if (response.ok) {
        setSubmitStatus("success");
        form.reset();
        setRecaptchaValue(null);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section {...storyblokEditable(blok)} className="bg-slate-100 py-16" data-sentry-component="ContactForm" data-sentry-source-file="ContactForm.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="ContactForm.tsx">
        <div className="max-w-2xl">
          <h2 className="mb-4 text-3xl font-semibold">{blok.title}</h2>
          <div className="mb-6">
            <RenderRichtext content={blok.subtitle} data-sentry-element="RenderRichtext" data-sentry-source-file="ContactForm.tsx" />
          </div>
          <Form {...form} data-sentry-element="Form" data-sentry-source-file="ContactForm.tsx">
            <form aria-label={t("formLabel")} className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-6 md:grid-cols-2">
                <FormField control={form.control} name="name" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t("nameLabel")}</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-white" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ContactForm.tsx" />
                <FormField control={form.control} name="email" render={({
                field
              }) => <FormItem>
                      <FormLabel>{t("emailLabel")}</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} className="bg-white" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ContactForm.tsx" />
              </div>
              <FormField control={form.control} name="message" render={({
              field
            }) => <FormItem>
                    <FormLabel>{t("messageLabel")}</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="min-h-[150px] bg-white" />
                    </FormControl>
                    <FormMessage />
                    <p className="mt-2 text-sm text-slate-500">
                      {field.value.length}/500 {t("characters")}
                    </p>
                  </FormItem>} data-sentry-element="FormField" data-sentry-source-file="ContactForm.tsx" />
              {cookiesAccepted ? <div className="flex justify-center">
                  <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""} onChange={value => setRecaptchaValue(value)} />
                </div> : <div className="text-center">
                  <p className="mb-2">{t("cookieConsentMessage")}</p>
                  <Button onClick={() => {
                localStorage.setItem("cookieConsent", "true");
                setCookiesAccepted(true);
              }}>
                    {t("acceptCookies")}
                  </Button>
                </div>}
              {submitStatus === "success" && <Alert variant="success">
                  <AlertTitle>{t("successTitle")}</AlertTitle>
                  <AlertDescription>{t("successMessage")}</AlertDescription>
                </Alert>}
              {submitStatus === "error" && <Alert variant="destructive">
                  <AlertTitle>{t("errorTitle")}</AlertTitle>
                  <AlertDescription>{t("errorMessage")}</AlertDescription>
                </Alert>}
              <Button className="w-full" disabled={isSubmitting || !recaptchaValue && !cookiesAccepted} size="lg" type="submit" data-sentry-element="Button" data-sentry-source-file="ContactForm.tsx">
                {isSubmitting ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("sending")}
                  </> : <>
                    <Send className="mr-2 h-4 w-4" />
                    {blok.submit_button_text || t("send")}
                  </>}
              </Button>
            </form>
          </Form>
        </div>
      </Container>
    </section>;
}